import { useCallback, useEffect, useMemo, useRef } from "react";

import Account from "byzantine/src/Account";

import { createFNVHashSync } from "../../../../components/authentication/utils";

import { useExternalAccountsStorage } from "./storage";

import type { AccountClassType } from "byzantine/src/Account";

/**
 * External accounts hidden on the old dashboard were saved in localStorage.
 * For the new dashboard, filter those out immediately and migrate them to
 * the real `hidden` flag in the background.
 */
export const useLegacyHiddenExternalAccounts = (
  userId: string,
  externalAccounts: AccountClassType[],
  updateAccount: (account: AccountClassType) => void
): Set<string> => {
  const [{ hiddenHashedAccountIds }, setExternalAccountsState] =
    useExternalAccountsStorage(userId);

  const updateAccountRef = useRef(updateAccount);
  updateAccountRef.current = updateAccount;
  // The effect re-runs on every context update, so skip accounts whose PUT
  // hasn't settled yet instead of sending it again.
  const inFlightAccountIds = useRef(new Set<string>());

  const clearStoredHash = useCallback(
    (accountId: string) => {
      const hash = createFNVHashSync(accountId);
      setExternalAccountsState((prev) => ({
        ...prev,
        hiddenHashedAccountIds: prev.hiddenHashedAccountIds.filter(
          (storedHash) => storedHash !== hash
        ),
      }));
    },
    [setExternalAccountsState]
  );

  useEffect(() => {
    if (hiddenHashedAccountIds.length === 0) return;

    const legacyHiddenSet = new Set(hiddenHashedAccountIds);

    externalAccounts
      .filter(
        (account) =>
          legacyHiddenSet.has(createFNVHashSync(account.id)) &&
          !inFlightAccountIds.current.has(account.id)
      )
      .forEach((account) => {
        // Already hidden server-side (e.g. migrated on another device) -
        // just drop the stale hash instead of toggling, which would unhide it.
        if (account.hidden) {
          clearStoredHash(account.id);
          return;
        }

        inFlightAccountIds.current.add(account.id);
        account
          .toggleHidden()
          .then((response) => {
            updateAccountRef.current(Account.deserialize(response.account));
            clearStoredHash(account.id);
          })
          .catch(() => {
            // Left in the stored set - retried on the next mount.
          })
          .finally(() => {
            inFlightAccountIds.current.delete(account.id);
          });
      });
  }, [externalAccounts, hiddenHashedAccountIds, clearStoredHash]);

  return useMemo(
    () => new Set(hiddenHashedAccountIds),
    [hiddenHashedAccountIds]
  );
};
